import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { protectedRoutes } from 'constants/routes'

import CheckIcon from '@mui/icons-material/Check'
import { AgGridReact } from 'ag-grid-react'
import { LoadingOverlay } from 'components/commonparts/index'
import { useGridBase } from 'hooks/useGridBase'
import { mediumFontSize } from 'style'

import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import type { User } from 'api-client'
import type { DataListStatus } from 'types/types'
import 'utils/mst.scss'

interface DataListProps {
  list: User
  isLoading: boolean
  listStatus: DataListStatus
  onListStatusChange: (status: DataListStatus) => void
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

// Custom cell renderer for enabled flag
function EnabledFlagRenderer(params: ICellRendererParams) {
  const { value } = params
  if (!value) return ''
  return <CheckIcon />
}

const commonAgGridStyles = {
  cellStyle: { fontSize: mediumFontSize },
  headerClass: 'ag-header-cell-custom',
}

function DataList({ list, isLoading, listStatus, onListStatusChange, gridRef: externalGridRef, findSearchValue: externalFindSearchValue, onFindChanged: externalOnFindChanged, isSearchVisible = false }: DataListProps) {
  const navigate = useNavigate()
  const internalGridRef = useRef<AgGridReact>(null)
  const gridRef = externalGridRef || internalGridRef
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  
  const {
    onGridReady,
    localeText,
    defaultColDef,
    autoSizeStrategy,
  } = useGridBase({
    gridRef,
    isLoading,
    storageKey: 'gridColumnState:/user-list',
  })

  // Handle window resize for responsive height calculation
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Calculate dynamic height based on data length and viewport
  const calculateGridHeight = useMemo(() => {
    const GRID_CONSTANTS = {
      rowHeight: 40,
      headerHeight: 42,
      paginationHeight: 52,
      extraPadding: 20,
      minRows: 3,
      maxViewportRatio: 0.7,
    }
    
    const minHeight = GRID_CONSTANTS.headerHeight + 
                     (GRID_CONSTANTS.rowHeight * GRID_CONSTANTS.minRows) + 
                     GRID_CONSTANTS.paginationHeight + 
                     GRID_CONSTANTS.extraPadding
    
    const maxHeight = Math.floor(windowHeight * GRID_CONSTANTS.maxViewportRatio)
    const maxRowsInViewport = Math.floor(
      (maxHeight - GRID_CONSTANTS.headerHeight - GRID_CONSTANTS.paginationHeight - GRID_CONSTANTS.extraPadding) / 
      GRID_CONSTANTS.rowHeight
    )
    
    if (!list.list || list.list.length === 0) {
      return minHeight
    }
    
    const dataRowsToShow = Math.min(list.list.length, listStatus.paginationModel.pageSize)
    
    if (dataRowsToShow <= maxRowsInViewport) {
      const exactHeight = GRID_CONSTANTS.headerHeight + 
                         (GRID_CONSTANTS.rowHeight * dataRowsToShow) + 
                         GRID_CONSTANTS.paginationHeight + 
                         GRID_CONSTANTS.extraPadding
      return Math.max(exactHeight, minHeight)
    }
    
    return maxHeight
  }, [list.list, listStatus.paginationModel.pageSize, windowHeight])

  // Container style with dynamic height based on data count and SearchBox visibility
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return {
      width: '100%',
      height: `${calculateGridHeight - searchBoxHeight}px`,
      fontSize: mediumFontSize,
    }
  }, [calculateGridHeight, isSearchVisible])

  // Enhanced default column definition using common styles
  const enhancedDefaultColDef = useMemo(() => ({
    ...defaultColDef,
    cellStyle: commonAgGridStyles.cellStyle,
    headerClass: commonAgGridStyles.headerClass
  }), [defaultColDef])

  // Column definitions with responsive flex layout
  const columnDefs = useMemo<ColDef[]>(() => [
    {
      field: 'userMail',
      headerName: 'メールアドレス',
      cellClass: 'link-cell', // Add class to style as link
      onCellClicked: (params) => {
        // Only redirect if clicking on the text
        const { event } = params;
        const target = event?.target as HTMLElement;
        
        if (target && target.classList.contains('ag-cell')) {
          const rect = target.getBoundingClientRect();
          const mouseEvent = event as MouseEvent;
          const clickX = mouseEvent.clientX;
          
          // Only redirect if clicking on the left half of the cell (where the text is)
          if (clickX - rect.left < rect.width / 2) {
            navigate(`/${protectedRoutes.UserDetail.path}?company_id=${params.data.companyId}&user_id=${params.data.userId}`)
          }
        }
      },
      flex: 2,
      minWidth: 250,
    },
    {
      headerName: 'ユーザー名',
      field: 'userName',
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: '権限区分',
      field: 'authTypeName',
      flex: 1,
      minWidth: 120,
    },
    {
      headerName: '有効',
      field: 'enabled',
      cellRenderer: EnabledFlagRenderer,
      flex: 0.5,
      minWidth: 80,
      maxWidth: 120,
    },
  ], [navigate])

  // Handle pagination changes and trigger API call
  const onPaginationChanged = useCallback(() => {
    if (!gridRef.current?.api) return
    
    const currentPage = gridRef.current.api.paginationGetCurrentPage()
    const pageSize = gridRef.current.api.paginationGetPageSize()
    
    if (currentPage !== listStatus.paginationModel.page || pageSize !== listStatus.paginationModel.pageSize) {
      const targetPage = pageSize !== listStatus.paginationModel.pageSize ? 0 : currentPage
      
      onListStatusChange({
        sortModel: listStatus.sortModel,
        paginationModel: { page: targetPage, pageSize }
      })
    }
  }, [listStatus.sortModel, listStatus.paginationModel.page, listStatus.paginationModel.pageSize, onListStatusChange])

  // Auto-fit columns to grid width when data loads
  useEffect(() => {
    if (!isLoading && gridRef.current?.api && list.list.length > 0) {
      try {
        const all = localStorage.getItem('gridColumnStates')
        const hasSaved = all && JSON.parse(all)['gridColumnState:/user-list']
        if (!hasSaved) {
          const timer = setTimeout(() => {
            gridRef.current?.api.sizeColumnsToFit()
          }, 100)
          return () => clearTimeout(timer)
        }
      } catch {
        const timer = setTimeout(() => {
          gridRef.current?.api.sizeColumnsToFit()
        }, 100)
        return () => clearTimeout(timer)
      }
    }
    return undefined
  }, [isLoading, list.list])

  // Hide pagination panel during loading to prevent incorrect information display
  useEffect(() => {
    const timer = setTimeout(() => {
      const paginationPanel = document.querySelector('.ag-paging-panel')
      if (paginationPanel) {
        (paginationPanel as HTMLElement).style.visibility = isLoading ? 'hidden' : 'visible'
      }
    }, 10)
    return () => clearTimeout(timer)
  }, [isLoading])

  return (
    <div style={containerStyle} className="ag-theme-balham">
      <AgGridReact
        ref={gridRef}
        rowData={list.list}
        columnDefs={columnDefs}
        defaultColDef={enhancedDefaultColDef}
        onGridReady={onGridReady}
        localeText={localeText}
        autoSizeStrategy={autoSizeStrategy}
        loadingOverlayComponent={LoadingOverlay}
        pagination
        paginationPageSize={listStatus.paginationModel.pageSize}
        paginationPageSizeSelector={[100, 300, 500]}
        findSearchValue={externalFindSearchValue}
        onFindChanged={externalOnFindChanged}
        onPaginationChanged={onPaginationChanged}
        getRowId={(params) => params.data.userId}
        suppressScrollOnNewData
        rowHeight={40}
        headerHeight={42}
        paginationAutoPageSize={false}
        domLayout="normal"
      />
    </div>
  )
}

export default DataList
